import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { getLanguagePref, updateLanguagePref } from "../api/TBIRequests";
import { DEFAULT_LANGUAGE, getLanguageDirection, isSupportedLanguage } from "./supportedLanguages";
import en from "./locales/en.json";
import es from "./locales/es.json";
import zhHans from "./locales/zh-Hans.json";
import id from "./locales/id.json";

const dictionaries = {
  en,
  es,
  "zh-Hans": zhHans, 
  id
};

// The DB stores "english"/"spanish" (unchanged backend). The app uses standard
// codes internally, so convert at the API boundary only.
const FROM_BACKEND = { english: "en", spanish: "es" };
const TO_BACKEND = { en: "english", es: "spanish" };
const fromBackend = (value) => FROM_BACKEND[value] || value;
const toBackend = (code) => TO_BACKEND[code] || code;

const TranslationContext = createContext({
  language: DEFAULT_LANGUAGE,
  setLanguage: () => {},
  t: (key) => key
});

export const TranslationProvider = ({ children, user }) => {
  const [language, setLanguageState] = useState(DEFAULT_LANGUAGE);

  useEffect(() => {
    const loadPreference = async () => {
      if (!user) return;
      const response = await getLanguagePref(user.userId);
      const savedLanguage = fromBackend(response.data?.language_preference);
      if (savedLanguage && isSupportedLanguage(savedLanguage)) {
        setLanguageState(savedLanguage);
      }
    };
    loadPreference().catch((err) => {
      console.error("Error loading language preference:", err);
    });
  }, [user]);

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = getLanguageDirection(language);
  }, [language]);

  const setLanguage = async (newLanguage) => {
    if (!isSupportedLanguage(newLanguage)) return;
    setLanguageState(newLanguage);
    if (user) {
      await updateLanguagePref({
        user_id: user.userId,
        language_pref: toBackend(newLanguage)
      });
    }
  };

  const value = useMemo(() => {
    const dictionary = dictionaries[language] || dictionaries[DEFAULT_LANGUAGE];
    const fallback = dictionaries[DEFAULT_LANGUAGE];
    return {
      language,
      setLanguage,
      t: (key, replacements = {}) => {
        let text = dictionary[key] || fallback[key] || key;
        Object.entries(replacements).forEach(([name, value]) => {
          text = text.replaceAll(`{{${name}}}`, value);
        });
        return text;
      }
    };
  }, [language]);

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = () => useContext(TranslationContext);